// components
import ColorSelector from '@components/ColorSelector/ColorSelector';
import { EmailSignatureFonts, SideEditorNodeKey } from '@components/EmailSignaturev2/types';
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
// hooks and context
import useNode from '@hooks/use-node';
import { analytics } from '@modules/analytics';
import { type FC, useContext, useEffect, useState } from 'react';
import Select from 'react-select';

import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditorDesignPanelSection.module.scss';

const EmailSignatureEditorThemeDesignSection: FC<React.PropsWithChildren<{ trackCustomColor?: boolean }>> = ({
  trackCustomColor = false,
}) => {
  const { emailSignature, emailSignatureDispatch, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const [signatureThemeSectionDiv, signatureThemeSectionDivRef] = useNode<HTMLDivElement>();
  const { registerNode, panelIndex } = useSideEditorContext();
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement>();

  const emailSignatureFontsOptions = Object.values(EmailSignatureFonts).map((offeredFont) => ({
    value: offeredFont,
    label: offeredFont,
  }));

  useEffect(() => setMenuPortalTarget(document.body), []);

  useEffect(() => {
    if (!signatureThemeSectionDiv) return;
    registerNode(SideEditorNodeKey.EMAIL_SIGNATURE_THEME_NODE, {
      panelBelongTo: panelIndex,
      node: signatureThemeSectionDiv,
    });
  }, [signatureThemeSectionDiv, registerNode, panelIndex]);

  return (
    <EmailSignatureEditorPanelSection ref={signatureThemeSectionDivRef}>
      <EmailSignatureEditorPanelSectionHeading heading={'Signature theme'} />

      <EmailSignatureEditorPanelSectionContents>
        <div className={styles['design-section-option']} data-direction="vertical">
          <div className={styles['design-section-option-label']}>Font</div>
          <Select
            //https://github.com/JedWatson/react-select/issues/2629
            id={'very long passage to fix the ssr bug in react-select'}
            instanceId={'very long passage to fix the ssr bug in react-select'}
            maxMenuHeight={150}
            components={{
              // biome-ignore lint/correctness/noNestedComponentDefinitions: historic
              IndicatorSeparator: () => null,
            }}
            defaultValue={emailSignatureFontsOptions[0]}
            styles={{
              container: () => ({ width: '100%', marginBottom: '-0.5rem' }),
              control: (base) => ({
                ...base,
                height: '2rem',
                width: '100%',
              }),
              option: (base, { data }) => ({ ...base, fontFamily: data.value }),
              singleValue: (base, { data }) => ({ ...base, fontFamily: data.value }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            value={{
              value: emailSignature.design.font,
              label: emailSignature.design.font,
            }}
            options={Object.values(EmailSignatureFonts).map((offeredFont) => ({
              value: offeredFont,
              label: offeredFont,
            }))}
            onChange={(chosenFont) =>
              chosenFont?.value &&
              emailSignatureDispatch({
                type: 'UPDATE_DESIGN_FONT',
                value: chosenFont.value,
              })
            }
            menuPortalTarget={menuPortalTarget}
            isDisabled={isSignatureLocked}
          />
        </div>

        <div className={styles['design-section-option']} data-direction="vertical">
          <div className={styles['design-section-option-label']}>Theme color</div>
          <div className={styles['design-section-color-selector-container']}>
            <ColorSelector
              color={emailSignature.design.themeColor.toUpperCase()}
              onSelect={(color) => {
                emailSignatureDispatch({
                  type: 'UPDATE_DESIGN_THEME_COLOR',
                  value: color,
                });
                if (emailSignature.saveContact?.buttonColorUseThemeColor) {
                  emailSignatureDispatch({
                    type: 'UPDATE_SAVE_BUTTON_COLOR',
                    value: color,
                  });
                }
              }}
              onCustomColorClicked={() => {
                trackCustomColor &&
                  analytics.track({
                    name: 'onboarding_signature_customcolor_clicked',
                  });
              }}
              onCustomColorClickedFreeUser={() => {
                analytics.track({
                  name: 'emailsignaturecustomcolorsector_clicked',
                });
              }}
              disabled={isSignatureLocked}
            />
          </div>
        </div>
        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit this styling, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorPanelSectionContents>
    </EmailSignatureEditorPanelSection>
  );
};

export default EmailSignatureEditorThemeDesignSection;
